import { ReactNode, useMemo } from "react";
import { Control, FieldError, FieldPath, FieldValues } from "react-hook-form";
import { useIntl } from "react-intl";

import AsyncSelect, { Option } from "@components/fields/AsyncSelect";
import useAsyncSearch from "@hooks/useAsyncSearch";
import useLazyQueryWithPromise from "@hooks/useLazyQueryWithPromise";
import {
  GetMedicalConditionsSearchDocument,
  GetMedicalConditionsSearchQuery,
  GetMedicalConditionsSearchQueryVariables
} from "@typing/Generated";

type Props<FormData extends FieldValues> = {
  control: Control<FormData>;
  disabled?: boolean;
  error: FieldError | undefined;
  initialOption: {
    label: string | undefined;
    value: string | null;
  };
  label?: string | ReactNode;
  name: FieldPath<FormData>;
  onSelection?: (option: { label: string; value: string }) => void;
  required?: boolean;
};

type MedicalCondition = NonNullable<
  NonNullable<GetMedicalConditionsSearchQuery["medicalConditionSearch"]["edges"]>[number]
>["node"];

const MedicalConditionSelect = <FormData extends FieldValues>({
  control,
  disabled = false,
  error,
  initialOption,
  label,
  name,
  onSelection,
  required = false
}: Props<FormData>) => {
  const intl = useIntl();

  const medicalConditionSearch = useLazyQueryWithPromise<
    GetMedicalConditionsSearchQuery,
    GetMedicalConditionsSearchQueryVariables
  >(GetMedicalConditionsSearchDocument);

  const defaultOptions = useMemo(
    () => [{ label: intl.formatMessage({ id: "dictionary.other" }), value: "other" }],
    [intl]
  );

  const medicalConditionAsyncSearch = useAsyncSearch<MedicalCondition>({
    defaultOptions,
    getLabel: t => t?.consumerName ?? "",
    getValue: t => t?.id ?? "",
    initialOption,
    sortOptions: false,
    takeDefaultLabel: false
  });

  return (
    <AsyncSelect<FormData>
      control={control}
      defaultOptions={medicalConditionAsyncSearch.defaultOptions}
      disabled={disabled}
      error={error}
      label={label}
      name={name}
      onSearch={async search =>
        medicalConditionAsyncSearch.searchOptions(
          search,
          medicalConditionSearch({ fetchPolicy: "no-cache", variables: { search } })
        )
      }
      onSelection={(option: Option) => {
        medicalConditionAsyncSearch.setCurrentOption(option);
        onSelection?.(option);
      }}
      required={required}
      value={medicalConditionAsyncSearch.currentOption}
    />
  );
};

export default MedicalConditionSelect;
